import type { ReactNode } from 'react';
import type { MiaMessageChannel } from '#services/api/mia.ts';
import type { Recipient, Status } from './OutreachDialog.tsx';

import { faCalendarPlus } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@mui/material';

import {
  Content,
  DeliverySummary,
  Footer,
  Header,
  MessagePreview,
  RecipientDetails,
  RecipientList,
  Root,
} from './OutreachDialog.tsx';

const channelLabels: Record<MiaMessageChannel, string> = {
  email: 'Email',
  sms: 'SMS',
};

interface ChannelPreview {
  body: ReactNode;
  channel: MiaMessageChannel;
  subject?: ReactNode;
}

interface CommonProps {
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  status: Status;
}

interface SingleRebookDialogProps extends CommonProps {
  preview: ChannelPreview;
  recipient: Recipient;
  recipients?: never;
  template?: never;
}

interface BulkRebookDialogProps extends CommonProps {
  excludedCount: number;
  preview?: never;
  recipient?: never;
  recipients: Recipient[];
  template: ReactNode;
}

type RebookDialogProps = SingleRebookDialogProps | BulkRebookDialogProps;

export function RebookDialog(props: RebookDialogProps) {
  if (props.recipients !== undefined) {
    const recipientNoun = props.recipients.length === 1 ? 'patient' : 'patients';
    const personalizationNote = 'Bracketed details will be personalized for each recipient.';
    // TODO(multi-channel): "mobile number on file" mirrors the UI's deliberate SMS-only gate (see
    // isSmsReachableRebookingRow in Readiness.tsx). Once email joins that gate, this reason and
    // the "Sent via SMS" summary below both need channel-neutral wording.
    const recipientNote =
      props.excludedCount > 0
        ? `${personalizationNote} Of the ${props.excludedCount + props.recipients.length} patients who need to rebook, ${props.excludedCount} will not be included because they do not currently need outreach or do not have a mobile number on file.`
        : personalizationNote;
    return (
      <Root onClose={props.onClose}>
        <Header color="warning" icon={faCalendarPlus}>
          Review rebooking outreach for {props.recipients.length} {recipientNoun}
        </Header>

        <Content>
          <MessagePreview label="Message Template">{props.template}</MessagePreview>
          <RecipientList note={recipientNote} recipients={props.recipients} />

          {/* Bulk regenerates copy server-side per recipient, so its wording can drift from this
              template. */}
          <Typography component="p" variant="caption" color="text.secondary" marginTop={1}>
            Coordinate generates the final copy when you send, so wording may vary from the preview.
          </Typography>

          {/* TODO(multi-channel): the server's bulk ask dispatches on every reachable channel,
              so once MIA_EMAIL_ENABLED flips on, patients with both contacts also get an email
              and this line under-reports. Accurate today (email is off in prod); revisit with
              the flag. */}
          <DeliverySummary>
            Sent via SMS · {props.recipients.length} {recipientNoun} will be contacted about
            rebooking
          </DeliverySummary>
        </Content>

        <Footer
          color="warning"
          confirmLabel={props.confirmLabel}
          icon={faCalendarPlus}
          onConfirm={props.onConfirm}
          status={props.status}
        />
      </Root>
    );
  }

  return (
    <Root onClose={props.onClose}>
      <Header color="warning" icon={faCalendarPlus}>
        Review rebooking outreach
      </Header>

      <Content>
        <RecipientDetails recipient={props.recipient} />

        <MessagePreview label={`${channelLabels[props.preview.channel]} Preview`}>
          {props.preview.subject ? (
            <Typography component="p" variant="body2" fontWeight={600} mb={1}>
              Subject: {props.preview.subject}
            </Typography>
          ) : null}
          {props.preview.body}
        </MessagePreview>

        <DeliverySummary>
          Sent via {channelLabels[props.preview.channel]} · Patient will be contacted about
          rebooking
        </DeliverySummary>
      </Content>

      <Footer
        color="warning"
        confirmLabel={props.confirmLabel}
        icon={faCalendarPlus}
        onConfirm={props.onConfirm}
        status={props.status}
      />
    </Root>
  );
}
